import LZString from 'lz-string';
import { ADMIN_ID, DEMO_EMAIL, seedDatabase, seedId, VENDOR_ID } from './seed.ts';
import { normalizeVqcaPsl } from './vqcaPsl.ts';
import { migrateVqcaSignNotes } from './vqcaSignNotes.ts';
import { migrateVqcaSnapshots } from './vqcaIntegrity.ts';

export type MockRecord = Record<string, unknown> & { oid: string };
export type MockDatabase = Record<string, MockRecord[]>;

const STORAGE_KEY = 'qcdoc.prototype.database.v2';
export const DATABASE_STORAGE_KEY = STORAGE_KEY;
type BackingStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
let storage: BackingStorage | undefined;
let database: MockDatabase | undefined;
let transactionActive = false;
let dirty = false;
let loading = false;
const compressedTables = new Map<string, { json: string; compressed: string }>();

/** A table whose serialized content changed in a save; `previous` is the content before the change. */
export interface SavedTable {
  table: string;
  previous: string | undefined;
  json: string;
  records: MockRecord[];
}
type SaveObserver = (changed: SavedTable[]) => void;
let saveObserver: SaveObserver | undefined;
let resetObserver: (() => void) | undefined;
const baselineJson = new Map<string, string>();
const systemBaseline = new Map<string, string>();
const idleTasks: Array<() => void> = [];

export function getBackingStorage(): BackingStorage {
  return storage ?? localStorage;
}

/** Registers the synchronization observer; pass undefined to detach it. */
export function setSaveObserver(observer: SaveObserver | undefined, onReset?: () => void): void {
  saveObserver = observer;
  resetObserver = observer ? onReset : undefined;
  baselineJson.clear();
  if (observer && database) captureBaseline();
}

/** Remembers the pristine content of tables that were never persisted, so the first edit can be diffed. */
function captureBaseline(): void {
  for (const [table, records] of Object.entries(database ?? {})) {
    if (Array.isArray(records) && !compressedTables.has(table) && !baselineJson.has(table)) baselineJson.set(table, JSON.stringify(records));
  }
}

/**
 * Marks the current in-memory content as system-generated (fixtures, migrations, cleanups) so the next
 * save does not report it as a user change. Each portal derives these deterministically on its own.
 */
export function adoptSystemChanges(): void {
  if (!saveObserver || !database) return;
  for (const [table, records] of Object.entries(database)) {
    if (!Array.isArray(records)) continue;
    const json = JSON.stringify(records);
    if ((systemBaseline.get(table) ?? compressedTables.get(table)?.json ?? baselineJson.get(table)) !== json) systemBaseline.set(table, json);
  }
}

/** Runs the task immediately, or after the active mock transaction settles. */
export function runWhenIdle(task: () => void): void {
  if (transactionActive) idleTasks.push(task);
  else task();
}

/** Discards the in-memory copy so the next read reloads what another tab persisted. */
export function reloadDatabase(): void {
  runWhenIdle(() => {
    database = undefined;
    compressedTables.clear();
  });
}

export function configureStorage(next: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>): void {
  storage = next;
  database = undefined;
  compressedTables.clear();
}

function migrateShipmentExceptionFixtures(data: MockDatabase): boolean {
  if (data.businessDemoSeed?.length) return false;
  const shipments = data.shipments ?? [];
  let migrated = false;
  for (let index = 100; index < 150; index += 1) {
    const shipment = shipments.find((record) => record.oid === seedId(75, index));
    if (shipment && !shipment.poNumber) {
      shipment.poNumber = `PO-${70000 + index}`;
      migrated = true;
    }
  }
  const existingIds = new Set(shipments.map((record) => record.oid));
  const fixtures = seedDatabase().shipments;
  for (let index = 150; index < 200; index += 1) {
    const fixture = fixtures.find((record) => record.oid === seedId(75, index));
    if (fixture && !existingIds.has(fixture.oid)) {
      shipments.push(structuredClone(fixture));
      migrated = true;
    }
  }
  return migrated;
}

function migrateSystemGeneratedVendorRequestFields(data: MockDatabase): boolean {
  let migrated = false;
  for (const request of data.vendorRequests ?? []) {
    const product = data.products?.find((item) => item.oid === request.productId);
    const requester = data.users?.find((user) => String(user.userId ?? user.oid) === String(request.requesterId));
    const category = data.categories?.find((item) => item.oid === request.catLevelId);
    if (!request.pslId && product?.pslID && normalizeVqcaPsl(request, product.pslID)) migrated = true;
    if (!request.labId && category?.labId) {
      request.labId = category.labId;
      migrated = true;
    }
    if (!request.catManagerId && typeof request.catMgrId === 'string' && request.catMgrId) {
      request.catManagerId = request.catMgrId;
      migrated = true;
    }
    if (Number(request.statusId ?? request.status) !== 7) continue;
    if (!request.profFName && requester?.firstName) {
      request.profFName = requester.firstName;
      migrated = true;
    }
    if (!request.profLName && requester?.lastName) {
      request.profLName = requester.lastName;
      migrated = true;
    }
    if (!request.catName && category?.name) {
      request.catName = category.name;
      migrated = true;
    }
  }
  return migrated;
}

/** Updates only the original deterministic product and specification fixtures; user-created records are untouched. */
function migratePslScopedProductFixtures(data: MockDatabase): boolean {
  if (data.businessDemoSeed?.length) return false;
  const migrationId = 'psl-scoped-product-spec-fixtures-1';
  if (data.adminMigrations?.some((row) => row.oid === migrationId)) return false;

  const fixtures = seedDatabase();
  const fixtureProducts = new Map(fixtures.products.map((record) => [record.oid, record]));
  const fixtureSpecs = new Map(fixtures.specs.map((record) => [record.oid, record]));
  let migrated = false;

  for (const product of data.products ?? []) {
    const fixture = fixtureProducts.get(product.oid);
    if (!fixture) continue;
    if (product.pslId === fixture.pslId && product.pslID === fixture.pslID && product.psl === fixture.psl) continue;
    product.pslId = fixture.pslId;
    product.pslID = fixture.pslID;
    product.psl = fixture.psl;
    migrated = true;
  }
  for (const spec of data.specs ?? []) {
    const fixture = fixtureSpecs.get(spec.oid);
    if (!fixture || spec.pslId === fixture.pslId) continue;
    spec.pslId = fixture.pslId;
    migrated = true;
  }

  (data.adminMigrations ??= []).push({ oid: migrationId, appliedAt: new Date().toISOString() });
  return migrated;
}

export function readDatabase(seed: () => MockDatabase = () => ({})): MockDatabase {
  if (database) return database;
  loading = true;
  try {
    return loadDatabase(seed);
  } finally {
    loading = false;
    if (saveObserver && database) captureBaseline();
  }
}

function loadDatabase(seed: () => MockDatabase): MockDatabase {
  const stored = (storage ?? localStorage).getItem(STORAGE_KEY);
  try {
    const parsed = stored ? JSON.parse(stored.startsWith('{') ? stored : LZString.decompressFromUTF16(stored)) : null;
    database = parsed?.format === 2
      ? Object.fromEntries(Object.entries(parsed.tables as Record<string, string>).map(([table, compressed]) => {
        const json = LZString.decompressFromUTF16(compressed);
        compressedTables.set(table, { json, compressed });
        return [table, JSON.parse(json)];
      }))
      : parsed ?? seed();
  } catch {
    throw new Error('Local mock data could not be read. Use Reset Mock Data to restore the seed.');
  }
  const legacyEmails = new Set<string>();
  for (const table of ['users', 'vendors']) {
    for (const record of database?.[table] ?? []) {
      if (record.oid !== ADMIN_ID && record.oid !== VENDOR_ID) continue;
      for (const value of [record.userName, record.email]) {
        if (typeof value !== 'string') continue;
        const email = value.trim().toLowerCase();
        if (email.endsWith('@halliburton.com') || (record.oid === VENDOR_ID && email === 'vendor@example.test')) legacyEmails.add(email);
      }
      if (record.oid === VENDOR_ID && record.password === 'Noman123@') record.password = 'Demo123@';
    }
  }
  let migrated = migrateShipmentExceptionFixtures(database!);
  if (migrateSystemGeneratedVendorRequestFields(database!)) migrated = true;
  if (migratePslScopedProductFixtures(database!)) migrated = true;
  if (legacyEmails.size > 0) migrated = true;
  if (migrateVqcaSignNotes(database!)) migrated = true;
  if (migrateVqcaSnapshots(database!)) migrated = true;
  const vendorAccounts = new Map((database?.users ?? []).map((record) => [record.oid, record]));
  for (const table of ['vqcas', 'vendorRequests']) {
    for (const record of database?.[table] ?? []) {
      const selectedUser = vendorAccounts.get(String(record.vendorId ?? ''));
      const vendorId = selectedUser?.isUser ? selectedUser.vendorId : undefined;
      if (typeof vendorId === 'string' && vendorId && record.vendorId !== vendorId) {
        record.vendorId = vendorId;
        migrated = true;
      }
    }
  }
  for (const table of ['vqcas', 'vendorRequests']) {
    for (const record of database?.[table] ?? []) {
      if (normalizeVqcaPsl(record)) migrated = true;
    }
  }
  const replaceIdentity = (value: unknown, field = ''): unknown => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      const replacement = legacyEmails.has(normalized) ? DEMO_EMAIL
        : /(?:firstName|fName)$/i.test(field) && normalized === 'syed' ? 'Test'
          : /(?:lastName|lName)$/i.test(field) && normalized === 'noman' ? 'User'
            : /^(?:name|fullName|description|createdBy|approvedBy|updatedBy|sourcePerson|qaSupervisor|generatedBy)$/i.test(field) && normalized === 'syed noman' ? 'Test User'
              : value;
      if (replacement !== value) migrated = true;
      return replacement;
    }
    if (Array.isArray(value)) return value.map((entry) => replaceIdentity(entry, field));
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      for (const [key, entry] of Object.entries(record)) {
        if (/password|answer|token|secret/i.test(key)) continue;
        const nameField = /^(?:old|new|oldValue|newValue)$/.test(key) && typeof record.field === 'string' ? record.field : key;
        record[key] = replaceIdentity(entry, nameField);
      }
    }
    return value;
  };
  replaceIdentity(database);
  if (migrated) saveDatabase();
  return database ??= seed();
}

export function saveDatabase(): void {
  if (transactionActive) { dirty = true; return; }
  const changed: SavedTable[] = [];
  try {
    const tables = Object.fromEntries(Object.entries(readDatabase()).map(([table, records]) => {
      const json = JSON.stringify(records);
      let entry = compressedTables.get(table);
      if (entry?.json !== json) {
        if (saveObserver && !loading && Array.isArray(records)) changed.push({ table, previous: systemBaseline.get(table) ?? entry?.json ?? baselineJson.get(table), json, records });
        entry = { json, compressed: LZString.compressToUTF16(json) };
        compressedTables.set(table, entry);
      }
      return [table, entry.compressed];
    }));
    (storage ?? localStorage).setItem(STORAGE_KEY, JSON.stringify({ format: 2, tables }));
    baselineJson.clear();
    systemBaseline.clear();
  } catch {
    throw new Error('Browser storage is full or unavailable. Remove attachments or reset mock data before retrying.');
  }
  if (changed.length) {
    try { saveObserver?.(changed); }
    catch (error) { console.error('Mock data synchronization failed to record a change.', error); }
  }
}

export async function transact<Result>(operation: () => Result | Promise<Result>): Promise<Result> {
  const previous = database ? structuredClone(database) : undefined;
  transactionActive = true;
  dirty = false;
  try {
    const result = await operation();
    transactionActive = false;
    if (dirty) saveDatabase();
    return result;
  } catch (error) {
    database = previous;
    systemBaseline.clear();
    throw error;
  } finally {
    transactionActive = false;
    dirty = false;
    while (idleTasks.length) idleTasks.shift()!();
  }
}

export function resetDatabase(): void {
  (storage ?? localStorage).removeItem(STORAGE_KEY);
  database = undefined;
  compressedTables.clear();
  baselineJson.clear();
  systemBaseline.clear();
  resetObserver?.();
}

export function releaseDatabase(): void {
  database = undefined;
}

export function pageRecords(records: MockRecord[], start = 0, size = 15, sortBy = '') {
  const [field, direction] = sortBy.split(' ');
  const sorted = [...records];
  if (field) {
    sorted.sort((left, right) => {
      const key = findSortKey(left, field);
      const leftValue = left[key];
      const rightValue = right[key];
      const leftNumber = toFiniteNumber(leftValue);
      const rightNumber = toFiniteNumber(rightValue);
      const leftDate = toTimestamp(leftValue);
      const rightDate = toTimestamp(rightValue);
      const comparison = leftNumber !== undefined && rightNumber !== undefined
        ? leftNumber - rightNumber
        : leftDate !== undefined && rightDate !== undefined
          ? leftDate - rightDate
          : String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      return direction?.toUpperCase() === 'DESC' ? -comparison : comparison;
    });
  }
  return { items: sorted.slice(Math.max(0, start), Math.max(0, start) + Math.max(0, size)), totalCount: records.length };
}

function findSortKey(record: MockRecord, field: string): string {
  const legacyFields: Record<string, string> = {
    QANUMBER: 'qaNumber',
    PRODNAME: 'prodName',
    VENDORCOMPANY: 'vendorCompany',
    CATLEVELNAME: 'catLevelName',
    DATERCVD: 'dateRcvd',
  };
  const candidate = legacyFields[field.toUpperCase()] ?? field;
  return Object.keys(record).find((name) => name.toLowerCase() === candidate.toLowerCase()) ?? candidate;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = Number(value.replaceAll(',', '').trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toTimestamp(value: unknown): number | undefined {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? undefined : timestamp;
}